import React, { useEffect, useMemo, useState } from 'react';
import { TeamInfo } from '../TeamInfo';
import { formatLapSeconds } from '../analytics/lapTime';
import {
  SectorHistory,
  SectorIndex,
  SectorStats,
  compareSectors,
  fieldBest,
  sectorSeries,
  sectorStats,
  teamSectorLaps,
} from '../analytics/sectorHistory';
import {
  applyFilter,
  categoriesOf,
  categoryOfPreset,
  isActive,
  markedPreset,
  toggleKart,
  topPreset,
} from '../analytics/sectorFilter';
import { useSectorFilter } from '../hooks/useSectorFilter';
import { SectorSeries, SectorTraceChart } from './charts/SectorTraceChart';
import { labelOf } from './ChartsPanel';
import './SectorsPanel.css';

interface SectorsPanelProps {
  teams: Map<string, TeamInfo>;
  history: SectorHistory;
  /** Bumped whenever the history changes, so the numbers are recomputed. */
  version: number;
  /** Highlighted teams: the default reference and the ones traced. */
  ourTeamIds: Set<string>;
  /**
   * Live only: fetch the laps that were run before the page was opened. A replay
   * rebuilds everything from its log and has nothing to fetch.
   */
  loadEarlier?: () => Promise<void>;
}

type View = 'table' | 'versus' | 'chart';
type Basis = 'best' | 'average';

const WINDOWS: { value: number; label: string }[] = [
  { value: 5, label: 'Last 5' },
  { value: 10, label: 'Last 10' },
  { value: 0, label: 'All laps' },
];

/** Traces get unreadable past a handful of overlaid lines. */
const MAX_TRACED = 6;

const SECTOR_NAMES = ['S1', 'S2', 'S3'] as const;

type SortKey =
  | 'position'
  | 'kart'
  | 'laps'
  | 'theoretical'
  | 'bestS1' | 'bestS2' | 'bestS3'
  | 'avgS1' | 'avgS2' | 'avgS3';

interface Row {
  team: TeamInfo;
  stats: SectorStats;
}

function fmt(seconds: number | null | undefined): string {
  return seconds === null || seconds === undefined ? '–' : formatLapSeconds(seconds);
}

function fmtDelta(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '–';
  if (Math.abs(seconds) < 0.0005) return '0.000';
  return `${seconds > 0 ? '+' : '−'}${Math.abs(seconds).toFixed(3)}`;
}

function positionOf(team: TeamInfo): number {
  const n = parseInt(team.position, 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

function sortValue(row: Row, key: SortKey): number {
  const { team, stats } = row;
  switch (key) {
    case 'position': return positionOf(team);
    case 'kart': return parseInt(team.kartNumber, 10) || Number.MAX_SAFE_INTEGER;
    case 'laps': return stats.laps;
    case 'theoretical': return stats.theoreticalBest ?? Number.POSITIVE_INFINITY;
    case 'bestS1': return stats.best?.[0] ?? Number.POSITIVE_INFINITY;
    case 'bestS2': return stats.best?.[1] ?? Number.POSITIVE_INFINITY;
    case 'bestS3': return stats.best?.[2] ?? Number.POSITIVE_INFINITY;
    case 'avgS1': return stats.average?.[0] ?? Number.POSITIVE_INFINITY;
    case 'avgS2': return stats.average?.[1] ?? Number.POSITIVE_INFINITY;
    case 'avgS3': return stats.average?.[2] ?? Number.POSITIVE_INFINITY;
  }
}

export const SectorsPanel: React.FC<SectorsPanelProps> = ({
  teams,
  history,
  version,
  ourTeamIds,
  loadEarlier,
}) => {
  const [view, setView] = useState<View>('table');
  const [lastN, setLastN] = useState(10);
  const [basis, setBasis] = useState<Basis>('best');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'position', desc: false });
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const [chartSector, setChartSector] = useState<SectorIndex>(0);
  const { filter, setFilter, clear } = useSectorFilter();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    void loadEarlier?.();
  }, [loadEarlier]);

  // Stats depend on the stored laps, not on every frame the teams map changes with.
  const statsByTeam = useMemo(() => {
    const out = new Map<string, SectorStats>();
    history.forEach((_entry, teamId) => {
      out.set(teamId, sectorStats(teamSectorLaps(history, teamId), lastN));
    });
    return out;
    // `version` is a deliberate dependency: the history is mutated in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, version, lastN]);

  /** Every team with at least one clean lap, by position. */
  const allRows = useMemo<Row[]>(() => {
    const list: Row[] = [];
    teams.forEach((team) => {
      const stats = statsByTeam.get(team.id);
      if (stats && stats.laps > 0) list.push({ team, stats });
    });
    return list.sort((a, b) => positionOf(a.team) - positionOf(b.team));
  }, [teams, statsByTeam]);

  const filtered = isActive(filter);
  const categories = useMemo(() => categoriesOf(allRows.map((r) => r.team)), [allRows]);

  /** What the person asked to see. */
  const rows = useMemo<Row[]>(() => {
    if (!filtered) return allRows;
    const keep = new Set(applyFilter(allRows.map((r) => r.team), filter).map((t) => t.id));
    return allRows.filter((r) => keep.has(r.team.id));
  }, [allRows, filter, filtered]);

  const field = useMemo(() => fieldBest(rows.map((r) => r.stats)), [rows]);
  const fieldAverage = useMemo(() => {
    const out: [number, number, number] = [Infinity, Infinity, Infinity];
    for (const r of rows) {
      r.stats.average?.forEach((v, i) => {
        if (v < out[i]) out[i] = v;
      });
    }
    return out;
  }, [rows]);

  const sortedRows = useMemo(() => {
    const dir = sort.desc ? -1 : 1;
    return [...rows].sort((a, b) => {
      const d = sortValue(a, sort.key) - sortValue(b, sort.key);
      if (d !== 0 && Number.isFinite(d)) return d * dir;
      return positionOf(a.team) - positionOf(b.team);
    });
  }, [rows, sort]);

  // The reference defaults to the first marked team that has laps, else the leader.
  //
  // It is looked for among all teams, not only the filtered ones: narrowing the list
  // must not make the thing everything is measured against vanish.
  const reference = useMemo<Row | null>(() => {
    if (referenceId) {
      const picked = allRows.find((r) => r.team.id === referenceId);
      if (picked) return picked;
    }
    const ours = allRows.find((r) => ourTeamIds.has(r.team.id));
    if (ours) return ours;
    return rows[0] ?? allRows[0] ?? null;
  }, [allRows, rows, referenceId, ourTeamIds]);

  /** The filtered teams plus the reference, for the views that measure against it. */
  const compareRows = useMemo<Row[]>(
    () =>
      reference && !rows.some((r) => r.team.id === reference.team.id)
        ? [reference, ...rows]
        : rows,
    [rows, reference],
  );

  const versusRows = useMemo(() => {
    if (!reference) return [];
    return compareRows
      .filter((r) => r.team.id !== reference.team.id)
      .map((r) => {
        const delta = compareSectors(reference.stats, r.stats);
        const values = basis === 'best' ? delta.best : delta.average;
        const total = values ? values[0] + values[1] + values[2] : null;
        return { row: r, values, total };
      })
      // Whoever beats the reference by most first: those are the ones to study.
      .sort((a, b) => (a.total ?? Infinity) - (b.total ?? Infinity));
  }, [compareRows, reference, basis]);

  /**
   * With a filter on, the chart follows it. Without one it keeps the old default:
   * the marked karts, or the top of the field when none are marked.
   */
  const tracedAll = useMemo<Row[]>(() => {
    if (filtered) {
      const rest = compareRows.filter((r) => r.team.id !== reference?.team.id);
      return reference ? [reference, ...rest] : rest;
    }
    const picked = allRows.filter((r) => ourTeamIds.has(r.team.id));
    if (reference && !picked.some((r) => r.team.id === reference.team.id)) picked.unshift(reference);
    return picked.length > 0 ? picked : allRows;
  }, [filtered, compareRows, allRows, ourTeamIds, reference]);
  const traced = useMemo(() => tracedAll.slice(0, MAX_TRACED), [tracedAll]);

  const series = useMemo<SectorSeries[]>(
    () =>
      traced.map(({ team }) => ({
        teamId: team.id,
        label: labelOf(team),
        kartNumber: team.kartNumber,
        values: sectorSeries(teamSectorLaps(history, team.id), chartSector),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [traced, history, version, chartSector],
  );

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: key === 'laps' },
    );

  const sortHeader = (key: SortKey, label: string, className?: string) => (
    <th
      className={`sp-sortable ${className ?? ''}`}
      aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      <button type="button" onClick={() => toggleSort(key)}>
        {label}
        {sort.key === key ? (sort.desc ? ' ▼' : ' ▲') : ''}
      </button>
    </th>
  );

  const windowLabel = WINDOWS.find((w) => w.value === lastN)?.label ?? 'All laps';
  const totalLaps = rows.reduce((n, r) => n + r.stats.laps, 0);

  const searchTerm = search.trim().toLowerCase();
  const pickable = searchTerm
    ? allRows.filter(
        (r) =>
          r.team.kartNumber.toLowerCase().includes(searchTerm) ||
          labelOf(r.team).toLowerCase().includes(searchTerm),
      )
    : allRows;
  const pickedKarts = new Set(filter.karts);

  return (
    <div className="sp-panel">
      <div className="sp-head">
        <h3>Sector times</h3>
        <div className="sp-tabs" role="tablist" aria-label="Sector view">
          {(
            [
              ['table', 'Teams'],
              ['versus', 'Head to head'],
              ['chart', 'Chart'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              className={`sp-tab ${view === id ? 'sp-tab--on' : ''}`}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="sp-note">
          {filtered ? `${rows.length} shown of ${allRows.length}` : `${rows.length} team${rows.length === 1 ? '' : 's'}`}
          {' '}· {totalLaps} clean laps · pit laps left out
        </span>
      </div>

      <div className="sp-filter">
        <button
          type="button"
          className={`sp-chip ${filter.karts.length > 0 ? 'sp-chip--on' : ''}`}
          aria-expanded={pickerOpen}
          onClick={() => setPickerOpen((open) => !open)}
        >
          Teams: {filter.karts.length > 0 ? `${rows.length} of ${allRows.length}` : 'all'} ▾
        </button>

        <label className="sp-filter-category">
          Category{' '}
          <select
            value={filter.category ?? ''}
            onChange={(e) => setFilter({ ...filter, category: e.target.value || null })}
          >
            <option value="">All</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <span className="sp-presets" role="group" aria-label="Quick selections">
          <button type="button" className="sp-chip" onClick={() => setFilter(markedPreset(allRows.map((r) => r.team), ourTeamIds))} disabled={ourTeamIds.size === 0}>
            Marked
          </button>
          <button type="button" className="sp-chip" onClick={() => setFilter(topPreset(allRows.map((r) => r.team), 5))}>
            Top 5
          </button>
          <button type="button" className="sp-chip" onClick={() => setFilter(topPreset(allRows.map((r) => r.team), 10))}>
            Top 10
          </button>
          <button
            type="button"
            className="sp-chip"
            onClick={() => setFilter(categoryOfPreset(reference?.team ?? null))}
            disabled={!reference?.team.category}
          >
            Reference's category
          </button>
          {filtered && (
            <button type="button" className="sp-chip sp-chip--clear" onClick={clear}>
              Clear filter
            </button>
          )}
        </span>
      </div>

      {pickerOpen && (
        <div className="sp-picker">
          <div className="sp-picker-bar">
            <input
              type="search"
              placeholder="Search kart or team"
              aria-label="Search teams"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className="sp-chip"
              onClick={() =>
                setFilter({
                  ...filter,
                  karts: Array.from(new Set([...filter.karts, ...pickable.map((r) => r.team.kartNumber)])),
                })
              }
            >
              Select {searchTerm ? 'matching' : 'all'}
            </button>
            <button type="button" className="sp-chip" onClick={() => setFilter({ ...filter, karts: [] })}>
              None
            </button>
          </div>
          <ul className="sp-picker-list">
            {pickable.map((r) => (
              <li key={r.team.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={pickedKarts.has(r.team.kartNumber)}
                    onChange={() => setFilter(toggleKart(filter, r.team.kartNumber))}
                  />
                  <span className="sp-picker-pos">{r.team.position || '–'}</span>
                  <span className="sp-picker-kart">#{r.team.kartNumber || '–'}</span>
                  <span className="sp-picker-name">{labelOf(r.team)}</span>
                  {r.team.category && <span className="sp-picker-cat">{r.team.category}</span>}
                </label>
              </li>
            ))}
            {pickable.length === 0 && <li className="sp-empty">No team matches that search.</li>}
          </ul>
          <p className="sp-legend">
            Nothing ticked shows every team. Your choice is remembered in this browser.
          </p>
        </div>
      )}

      <div className="sp-controls">
        {view !== 'chart' && (
          <label>
            Average over{' '}
            <select value={lastN} onChange={(e) => setLastN(Number(e.target.value))}>
              {WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </label>
        )}

        {view === 'versus' && (
          <label>
            Compare by{' '}
            <select value={basis} onChange={(e) => setBasis(e.target.value as Basis)}>
              <option value="best">Best sector</option>
              <option value="average">{windowLabel.toLowerCase()} average</option>
            </select>
          </label>
        )}

        {view !== 'table' && (
          <label>
            Reference{' '}
            <select
              value={reference?.team.id ?? ''}
              onChange={(e) => setReferenceId(e.target.value || null)}
            >
              {reference === null && <option value="">–</option>}
              {[...compareRows]
                .sort((a, b) => positionOf(a.team) - positionOf(b.team))
                .map((r) => (
                  <option key={r.team.id} value={r.team.id}>
                    #{r.team.kartNumber} {labelOf(r.team)}
                  </option>
                ))}
            </select>
          </label>
        )}

        {view === 'chart' && (
          <div className="sp-tabs" role="tablist" aria-label="Sector">
            {SECTOR_NAMES.map((name, i) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={chartSector === i}
                className={`sp-tab ${chartSector === i ? 'sp-tab--on' : ''}`}
                onClick={() => setChartSector(i as SectorIndex)}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {allRows.length > 0 && rows.length === 0 && view === 'table' && (
        <div className="sp-empty">
          No team with sector times matches the filter.{' '}
          <button type="button" className="sp-chip" onClick={clear}>Clear filter</button>
        </div>
      )}

      {allRows.length === 0 && (
        <div className="sp-empty">
          No sector times yet. They are taken as each team completes a lap, so this fills in
          within a minute or two{loadEarlier ? ' (earlier laps are loaded from the server)' : ''}.
        </div>
      )}

      {rows.length > 0 && view === 'table' && (
        <div className="sp-scroll">
          {filtered && (
            <p className="sp-legend sp-legend--top">Green marks the best among the teams shown.</p>
          )}
          <table className="sp-table">
            <thead>
              <tr className="sp-group">
                <th colSpan={4} />
                <th colSpan={3}>S1</th>
                <th colSpan={3}>S2</th>
                <th colSpan={3}>S3</th>
                <th />
              </tr>
              <tr>
                {sortHeader('position', 'Pos')}
                {sortHeader('kart', 'Kart')}
                <th className="sp-left">Team</th>
                {sortHeader('laps', 'Laps')}
                {sortHeader('bestS1', 'Best')}
                {sortHeader('avgS1', 'Avg')}
                <th>Last</th>
                {sortHeader('bestS2', 'Best')}
                {sortHeader('avgS2', 'Avg')}
                <th>Last</th>
                {sortHeader('bestS3', 'Best')}
                {sortHeader('avgS3', 'Avg')}
                <th>Last</th>
                {sortHeader('theoretical', 'Ideal lap')}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(({ team, stats }) => (
                <tr key={team.id} className={ourTeamIds.has(team.id) ? 'sp-ours' : undefined}>
                  <td>{team.position || '–'}</td>
                  <td>{team.kartNumber || '–'}</td>
                  <td className="sp-left sp-name">{labelOf(team)}</td>
                  <td>{stats.laps}</td>
                  {([0, 1, 2] as const).map((i) => (
                    <React.Fragment key={i}>
                      <td className={field && stats.best?.[i] === field[i] ? 'sp-best' : undefined}>
                        {fmt(stats.best?.[i])}
                      </td>
                      <td className={stats.average && stats.average[i] === fieldAverage[i] ? 'sp-best' : undefined}>
                        {fmt(stats.average?.[i])}
                      </td>
                      <td className="sp-dim">{fmt(stats.last?.[i])}</td>
                    </React.Fragment>
                  ))}
                  <td>{fmt(stats.theoreticalBest)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {allRows.length > 0 && view === 'versus' && reference && (
        <div className="sp-scroll">
          <table className="sp-table">
            <thead>
              <tr>
                <th>Pos</th>
                <th>Kart</th>
                <th className="sp-left">Team</th>
                <th>S1</th>
                <th>S2</th>
                <th>S3</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="sp-ref">
                <td>{reference.team.position || '–'}</td>
                <td>{reference.team.kartNumber || '–'}</td>
                <td className="sp-left sp-name">{labelOf(reference.team)} (reference)</td>
                {([0, 1, 2] as const).map((i) => (
                  <td key={i}>
                    {fmt(basis === 'best' ? reference.stats.best?.[i] : reference.stats.average?.[i])}
                  </td>
                ))}
                <td>
                  {fmt(
                    basis === 'best'
                      ? reference.stats.theoreticalBest
                      : reference.stats.average
                        ? reference.stats.average[0] + reference.stats.average[1] + reference.stats.average[2]
                        : null,
                  )}
                </td>
              </tr>
              {versusRows.map(({ row, values, total }) => (
                <tr key={row.team.id}>
                  <td>{row.team.position || '–'}</td>
                  <td>{row.team.kartNumber || '–'}</td>
                  <td className="sp-left sp-name">{labelOf(row.team)}</td>
                  {([0, 1, 2] as const).map((i) => (
                    <td key={i} className={deltaClass(values?.[i])}>{fmtDelta(values?.[i])}</td>
                  ))}
                  <td className={deltaClass(total)}>{fmtDelta(total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="sp-legend">
            Green: the reference is faster by that much in the sector. Red: the other team is
            faster. Teams that beat the reference most are listed first.
          </p>
        </div>
      )}

      {allRows.length > 0 && view === 'chart' && (
        <>
          <SectorTraceChart series={series} sectorLabel={SECTOR_NAMES[chartSector]} />
          <p className="sp-legend">
            {filtered
              ? 'The filtered teams and the reference'
              : ourTeamIds.size > 0
                ? 'Your marked karts and the reference'
                : 'The top of the field'}
            {' '}— {SECTOR_NAMES[chartSector]} per clean lap.
            {tracedAll.length > MAX_TRACED &&
              ` Showing ${MAX_TRACED} of ${tracedAll.length}; narrow the filter to see others.`}
          </p>
        </>
      )}
    </div>
  );
};

/**
 * Delta is other − reference, so a positive number means the reference is faster,
 * which is the good outcome when the reference is the team being looked after.
 */
function deltaClass(value: number | null | undefined): string | undefined {
  if (value === null || value === undefined || Math.abs(value) < 0.0005) return undefined;
  return value > 0 ? 'sp-gain' : 'sp-loss';
}

export default SectorsPanel;
